import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { apiPost } from "../lib/api";

/**
 * "I'm locked out." Companion to the in-app Change password dialog, which is no
 * help to someone who can't sign in.
 *
 * The confirmation screen deliberately does NOT tell you whether the address was
 * on an account — the server returns the same acknowledgement either way, and the
 * UI must not undo that by rendering a different state for the two cases. So
 * there is only one success screen, and its wording is conditional-by-design
 * ("if that address is on an account").
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiPost("/api/auth/forgot-password", {
        email: email.trim().toLowerCase(),
      });
      setSent(true);
    } catch {
      // The route swallows its own failures, so reaching here means the request
      // never landed — a network problem, not a rejected address.
      setError("Couldn't reach the server. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold text-slate-900">Savvy Reputation</h1>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {sent ? (
            <>
              <h2 className="text-base font-semibold text-slate-900">Check your email</h2>
              <p className="mt-2 text-sm text-slate-600">
                If that address is on an account, we've sent it a link to set a new
                password. It's good for about an hour and can be used once.
              </p>
              <p className="mt-2 text-sm text-slate-500">
                Nothing after a few minutes? Check your spam folder, then try again.
              </p>
              <Link
                to="/login"
                className="mt-5 block w-full rounded-lg bg-brand-600 px-3 py-2 text-center text-sm font-medium text-white hover:bg-brand-700"
              >
                Back to sign in
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-base font-semibold text-slate-900">
                Reset your password
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Enter the email you sign in with and we'll send a reset link.
              </p>
              <form onSubmit={onSubmit} className="mt-5 space-y-4">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Email</span>
                  <input
                    type="email"
                    autoComplete="username"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 shadow-inner focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  />
                </label>
                {error && (
                  <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-xs text-slate-500 underline hover:text-slate-700"
                  >
                    Back to sign in
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
